import { CloseOutlined, DownOutlined, PlusCircleOutlined, SwapRightOutlined } from '@ant-design/icons';
import { ProFormField } from '@ant-design/pro-form';
import {
  Input,
  Dropdown,
  Space,
  Select,
  Button,
  InputNumber,
  Form,
  Tooltip,
  FormInstance,
  AutoComplete,
  Spin,
} from 'antd';
import classNames from 'classnames';
import { get, remove, isEmpty, isArray } from 'lodash-es';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DATA_TID } from '@/infrastructure/cypress-ids';
import { getProductId } from '@/infrastructure/product-info';
import { FormEntry } from '@/presentation/components/form';
import { getBuglyCustomKeys, getCustomUserKeys, getExtendInfoKeys } from '@/presentation/components/form/api';
import {
  FieldsOperator,
  fieldsOperators,
  FieldValueType,
} from '@/presentation/components/form/proto';
import { CHARACTER_MATCH_OPTIONS, MATCH_MODE_TEXT, MatchMode } from '@/presentation/components/form/standards/types';
import { IssueType } from '@/proto/issue';
import { KeyDataType } from '@/proto/token';
import { IUserKey } from '@/proto/user';
import { toLineString } from '@raft/utils/src/case/snake-case';
import styles from './custom-fields.module.scss';
import type { FormItemProps } from 'antd/lib/form/FormItem';

export interface IProps {
  defaultValue?: any;
  value?: any;
  entry: FormEntry;
  issueType?: IssueType;
  type: CustomType;
}

export interface CustomFieldsItemProps {
  index: number;
  columns?: IUserKey[];
  onDelete?: (index: number) => void;
  form: FormInstance;
  onChange?: (values: CustomFieldValue, index: number) => void;
  type: CustomType;
  loading?: boolean;
  name: string;
  keyBaseProps?: React.ComponentProps<typeof Select> | React.ComponentProps<typeof AutoComplete>;
  operatorBaseProps?: React.ComponentProps<typeof Select>;
  operatorOptions?: React.ComponentProps<typeof Select>['options'];
  valueBaseProps?: React.ComponentProps<typeof Input>
  | React.ComponentProps<typeof Select>
  | React.ComponentProps<typeof InputNumber>;
}

export interface CustomFieldsPanelProps {
  defaultValue?: string;
  value?: CustomFieldValue[];
  onChange?: (value: CustomFieldValue[]) => void;
  name: string;
  issueType: IssueType;
  type: CustomType;
  className?: string;
  keys?: CustomKey[]
  // 传递给触发下拉面板的外层输入框的属性
  triggerInputProps?: React.ComponentProps<typeof Input>;
  // 应用于所有键选择器（Select/AutoComplete）的基础属性
  keyBaseProps?: React.ComponentProps<typeof Select> | React.ComponentProps<typeof AutoComplete>;
  // 应用于所有运算符选择框的基础属性
  operatorBaseProps?: React.ComponentProps<typeof Select>;
  // 自定义运算符选项（若提供，将不再推断）
  operatorOptions?: React.ComponentProps<typeof Select>['options'];
  // 应用于所有值输入控件（Input/Select/InputNumber）的基础属性
  valueBaseProps?: React.ComponentProps<typeof Input>
  | React.ComponentProps<typeof Select>
  | React.ComponentProps<typeof InputNumber>;

}

export type CustomType = 'customKey' | 'customFields' | 'buglyCustomKey';

export type CustomKey = {
  label: string;
  key: string;
  type: number;
  valueSelectProps?: any;
  valueFormItemProps?: Partial<FormItemProps>;
};

export type CustomFieldValue = {
  operator?: string;
  key?: string;
  values?: string[] | string;
  type?: KeyDataType;
  name?: string;
};

// cypress 测试标识
const { addBtn, confirmBtn, cancelBtn, itemDelete, itemKey, itemOP, itemValue } = DATA_TID.customField;

const defaultFilter = {
  name: undefined,
  operator: undefined,
  values: undefined,
};

type RangeValue =  [number | undefined, number | undefined];

const InputNumberRange = (props: {
  onChange?: (rangeValue: RangeValue) => void;
  value?: RangeValue;
  defaultValue?: RangeValue;
  className?: string;
}) => {
  const { defaultValue = [undefined, undefined] } = props;
  const [rangeValue, setRangeValue] = useState<RangeValue>(props.value || defaultValue);

  const onChage = (value: number, index: number) => {
    const range = [...rangeValue];
    range[index] = value;
    setRangeValue(range as RangeValue);
    props.onChange?.(range as RangeValue);
  };

  useEffect(() => {
    // 刷新页面后解析token得到的rangeValue是['x,y']这种形式，不满足[x,y]，需要特殊处理以正确回显。
    if (typeof rangeValue?.[0] === 'string') {
      const rangeValueTransform = [Number((rangeValue?.[0] as string).split(',')[0]), Number((rangeValue?.[0] as string).split(',')[1])];
      setRangeValue(rangeValueTransform as RangeValue);
    } else {
      setRangeValue(rangeValue);
    }
  }, [props.value]);

  return (
    <div className={classNames(props.className, styles.inputRange)}>
      <InputNumber
        value={rangeValue[0]}
        onChange={value => onChage(value as number, 0)}
        data-tid={itemValue.numberFrom}
      />
      <SwapRightOutlined />
      <InputNumber
        value={rangeValue[1]}
        onChange={value => onChage(value as number, 1)}
        data-tid={itemValue.numberTo}
      />
    </div>
  );
};

const CustomFieldsItem: React.FC<CustomFieldsItemProps> = (props) => {
  const { columns = [], form, index, type } = props;
  const [selectedKey, setSelectedKey] = useState<CustomKey | null>(null);
  const [operator, setOperator] = useState<FieldsOperator | null>(null);

  const KeyComponent = type === 'buglyCustomKey' ? AutoComplete : Select;

  const onDelete = () => {
    props.onDelete?.(props.index);
  };

  const customPrefix = `${props.name}`;

  const operatorOptions: any = useMemo(
    () => {
      if (props.operatorOptions && props.operatorOptions?.length > 0) {
        return (props.operatorOptions as any[]).map((item: any) => ({
          ...item,
          ['data-tid']: `${customPrefix}-operator-${item.label}-${index}`,
        }));
      }
      if (type === 'buglyCustomKey') {
        return CHARACTER_MATCH_OPTIONS.map(item => ({
          ...item,
          ['data-tid']: `${customPrefix}-operator-${item.label}-${index}`,
        }));
      }
      const options = fieldsOperators[selectedKey?.type as FieldValueType] ?? [];
      return options.map(item => ({
        ...item,
        ['data-tid']: `${customPrefix}-operator-${item.label}-${index}`,
      }));
    },
    [selectedKey, props.operatorOptions],
  );


  const customKeysOptions = useMemo(() => columns.map(item => ({
    label: item.label,
    value: item.key,
    ['data-tid']: `${customPrefix}-name-${item.label}-${index}`,
  })), [columns]);

  const onKeyChange = (value: unknown) => {
    const key = String(value);
    const currentKey = columns?.find(item => item.key === key);
    setSelectedKey(currentKey as CustomKey);
    form.setFieldsValue({
      key: currentKey?.key || key,
      type: currentKey?.type ?? KeyDataType.STRING,
    });
    if (currentKey?.type !== undefined && type !== 'buglyCustomKey') {
      const options = props.operatorOptions
        ? props.operatorOptions
        : get(fieldsOperators, currentKey?.type as KeyDataType);
      form.setFieldsValue({
        operator: options?.[0]?.value,
      });
    }
  };

  const onOperatorChange = (value: unknown) => {
    const operator = String(value);
    const op = operator === '' ? FieldsOperator.Eq : (operator as FieldsOperator);
    setOperator(op);
    form.setFieldsValue({
      values: undefined,
      operator: op,
    });
  };

  const renderFieldValueItem = () => {
    const { type, valueSelectProps } = selectedKey || {};
    const componentBaseProps = {
      ...valueSelectProps,
      ...props.valueBaseProps,
    };
    if (type === KeyDataType.NUMBER) {
      return operator === FieldsOperator.Between ? (
        <InputNumberRange className={styles.rangeValues}  {...componentBaseProps} />
      )
        : (
        <InputNumber
          className={styles.values}
          placeholder={'Value'}
          data-tid={`${itemValue.number}-${index}`}
          {...componentBaseProps}
        />
        );
    }
    if (type === KeyDataType.ARRAY) {
      return <Select
        className={styles.values}
        mode="tags"
        open={false}
        placeholder={'Value'}
        maxTagCount='responsive'
        showSearch
        data-tid={`${itemValue.array}-${index}`}
        {...componentBaseProps}
      />;
    }

    // 使用 TEXT 类型渲染输入框
    if (type === FieldValueType.TextInput) {
      return <Input
        className={styles.values}
        placeholder={'Value'}
        data-tid={`${itemValue.default}-${index}`}
        disabled={[MatchMode.ISEMPTY, MatchMode.NOTEMPTY].includes(operator as any)}
        {...componentBaseProps}
      />;
    }

    return <Select
      mode={'tags'}
      className={styles.values}
      placeholder={'Value'}
      data-tid={`${itemValue.default}-${index}`}
      disabled={[MatchMode.ISEMPTY, MatchMode.NOTEMPTY].includes(operator as any)}
      {...componentBaseProps}
    />;
  };

  useEffect(() => {
    const value = form.getFieldsValue();
    value && onKeyChange(value.key);
    value.operator !== undefined && onOperatorChange(value.operator);
    // 重新设置一次值，让更新的选项可以选上
    form.setFieldsValue({
      ...value,
      operator: value.operator === '' ? FieldsOperator.Eq : (value.operator as FieldsOperator),
    });
  }, []);
  return (
    <Form
      preserve
      form={form}
      className={styles.customForm}
      onFieldsChange={() => {
        props.onChange?.(form.getFieldsValue(), index);
      }}>
      <Form.Item hidden name='type' />
      <Space size={8}>
        <Form.Item
          name='key'
        >
          <KeyComponent
            filterOption={(inputValue, option) => {
              const labelText = String((option as any)?.label ?? '');
              return labelText.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1;
            }}
            className={styles.column}
            options={customKeysOptions}
            placeholder='Key或别名'
            onChange={onKeyChange}
            showSearch
            dropdownMatchSelectWidth={250}
            data-tid={`${itemKey}-${index}`}
            {...props.keyBaseProps}
          />
        </Form.Item>
        <Form.Item
          name='operator'
          initialValue={operatorOptions?.[0]?.value}
        >
          <Select
            options={operatorOptions}
            className={styles.operator}
            placeholder='请选择'
            onChange={onOperatorChange}
            data-tid={`${itemOP}-${index}`}
            {...props.operatorBaseProps}
          />
        </Form.Item>
        <Form.Item
          name='values'
          {...(selectedKey?.valueFormItemProps || {})}
        >
          {renderFieldValueItem()}
        </Form.Item>
        <CloseOutlined data-tid={`${itemDelete}-${index}`} className={styles.close} onClick={onDelete}/>
      </Space>
    </Form>
  );
};

export const CustomFieldsPanel: React.FC<CustomFieldsPanelProps> = (props) => {
  const { onChange, name, issueType, type, className, triggerInputProps } = props;
  const [visible, setVisible] = useState(false);
  const [customKeys, setCustomKeys] = useState<IUserKey[]>(props.keys || []);
  const [customFilters, setCustomFilters] = useState<CustomFieldValue[]>([defaultFilter]);
  const [filterText, setFilterText] = useState('');
  const [loading, setLoading] = useState(false);
  const forms = useRef<FormInstance[]>([]);
  const [loaded, setLoaded] = useState(false);

  const showOverlay = () => setVisible(true);
  const hideOverlay = () => setVisible(false);

  const FieldItemContainer = (fiProps: {
    index: number,
    onDelete?: (index: number) => void
  }) => {
    const { index, onDelete } = fiProps;
    const [form] = Form.useForm();

    if (!forms.current[index]) {
      forms.current[index] = form;
      forms.current[index].setFieldsValue(customFilters[index]);
    }

    return (
      <CustomFieldsItem
        form={forms.current[index]}
        index={index}
        columns={customKeys}
        onDelete={onDelete}
        type={type}
        loading={loading}
        name={name}
        keyBaseProps={props.keyBaseProps}
        operatorBaseProps={props.operatorBaseProps}
        operatorOptions={props.operatorOptions}
        valueBaseProps={props.valueBaseProps}
      />
    );
  };

  const onDelete = (index: number) => {
    const tempData = [...customFilters];
    const tempForms = [...forms.current];
    remove(tempData, (_: CustomFieldValue, i: number) => index === i);
    remove(tempForms, (_: FormInstance, i: number) => index === i);
    forms.current = tempForms;
    setCustomFilters(tempData);
  };

  const renderFilters = () => customFilters.map((_filter, index) => (
    <FieldItemContainer index={index} onDelete={onDelete} />
  ));

  const addFilter = () => {
    setCustomFilters([
      ...customFilters,
      defaultFilter,
    ]);
  };

  const getFilterValue = (item: CustomFieldValue) => {
    if ([MatchMode.ISEMPTY, MatchMode.NOTEMPTY].includes(item?.operator as any)) {
      return [];
    }
    const stringValue = `${item?.values as string}`;
    if (!isArray(item.values)) return stringValue?.split(';');
    if (item?.operator === FieldsOperator.Between) return stringValue?.split(';');

    return item?.values;
  };

  const filterEmptyValues = (fieldValues: CustomFieldValue[]) => {
    const values: CustomFieldValue[] = [];
    fieldValues.forEach((item) => {
      const isValidFilterItem = (item.key && item.operator && item.values)
        || [MatchMode.ISEMPTY, MatchMode.NOTEMPTY].includes(item.operator as any);


      if (isValidFilterItem) {
        values.push({
          ...item,
          values: getFilterValue(item),
          name: toLineString(name),
          operator: item.operator === FieldsOperator.Eq ? '' : item.operator,
        });
      }
    });

    return values;
  };

  const getFilterText = (values: CustomFieldValue[]) => values.map((item) => {
    const operatorText = MATCH_MODE_TEXT[(item.operator === '' ? MatchMode.Eq : item.operator) as string];
    const currentKey = customKeys.find(customKey => customKey.key === item.key) as unknown as CustomKey;
    let displayValues = [MatchMode.ISEMPTY, MatchMode.NOTEMPTY].includes(item.operator as any) ? '' : JSON.stringify(item.values);

    // 处理边界case，外部给数值控件传入options的情况⬇️
    // 此时对外展示的值，应该是label，而不是value，需要找出来并展示
    const valueOptions = currentKey?.valueSelectProps?.options;
    if (valueOptions && item.values) {
      const values = Array.isArray(item.values) ? item.values : [item.values];
      const items = values
        .map(v => valueOptions.find((opt: {value: string}) => String(opt.value) === String(v)))
        .map(item => item.label);
      displayValues = JSON.stringify(items);
    }

    return `${currentKey?.label || item?.key} 的Value ${operatorText} ${displayValues}`;
  }).join(' 且 ');

  const onConfirm = async () => {
    try {
      const formItemValues = forms.current.map(form => form.getFieldsValue());
      const values = filterEmptyValues(formItemValues);
      setFilterText(getFilterText(values));
      hideOverlay();
      onChange?.(values);
    } catch (e) {
    }
  };

  const overlay = (
      <div className={styles.customOverlay}>
        <Spin spinning={loading} tip='加载自定义字段中'>
          <span>以下的关系为全且</span>
          <div className={styles.itemContainer}>
            {renderFilters()}
          </div>
          <div>
            <Space className={styles.addButton} size={3}>
              <PlusCircleOutlined/>
              <span data-tid={addBtn} onClick={addFilter}>添加筛选</span>
            </Space>
          </div>
          <div className={styles.footer}>
            <Space size={10}>
              <Button data-tid={cancelBtn} onClick={hideOverlay}>取消</Button>
              <Button data-tid={confirmBtn} type={'primary'} onClick={onConfirm}>确定</Button>
            </Space>
          </div>
        </Spin>
      </div>
  );

  useEffect(() => {
    const load = async () => {
      try {
        const service = props.type === 'customKey' ? getExtendInfoKeys : getCustomUserKeys;
        setLoading(true);
        if (props.keys) {
          setCustomKeys(props.keys);
        } else if (type === 'buglyCustomKey') {
          const keys = await getBuglyCustomKeys();
          setCustomKeys(keys.map((key: string) => ({
            label: key,
            key,
            type: 1,
          })));
        } else {
          const keys = await service({
            productId: getProductId(),
            issueType,
          });

          setCustomKeys(keys);
        }

        setLoading(false);
      } catch (e) {
        // 待处理
      } finally {
        setLoaded(true);
      }
    };

    (visible && !loaded) && load();
  }, [visible]);

  useEffect(() => {
    if (props.value && !isEmpty(props.value)) {
      setCustomFilters(props.value as CustomFieldValue[]);
      setFilterText(getFilterText(props.value as CustomFieldValue[]));
    }
  }, [props.value]);

  return (
    <div className={className ? className : styles.panel}>
      <Space size={0} className={styles.panelSpace} direction={'vertical'}>
        <Tooltip title={filterText}>
          <Input
            placeholder='多个值通过按回车分隔'
            onFocus={showOverlay}
            suffix={<DownOutlined style={{ color: 'rgba(0, 0, 0, 0.25)', fontSize: '12px' }}/>}
            style={{ width: '100%' }}
            value={filterText}
            {...triggerInputProps}
          />
        </Tooltip>
        <Dropdown
          overlay={overlay}
          trigger={['click']}
          visible={visible}
          destroyPopupOnHide={false}
          onVisibleChange={visible => setVisible(visible)}
        >
          <div style={{ height: 1 }}/>
        </Dropdown>
      </Space>
    </div>
  );
};

export const CustomFields: React.FC<IProps & { hidden?: boolean }> = (props) => {
  const { entry, hidden } = props;
  const { name, label, keys } = entry;

  return (
    <ProFormField name={name} label={label} hidden={hidden}>
      <CustomFieldsPanel
        issueType={props.issueType as IssueType}
        name={name}
        type={props.type}
        keys={keys as CustomKey[]}
        keyBaseProps={(entry as any)?.keyBaseProps}
        operatorBaseProps={(entry as any)?.operatorBaseProps}
        valueBaseProps={(entry as any)?.valueBaseProps}
      />
    </ProFormField>
  );
};
